import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash, Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { Modal, Popconfirm, Tabs } from "antd";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  SchemaService,
  DynamicModuleService,
  DropdownService,
} from "@/services/APIs";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import LoadingSpinner from "@/components/common/LoadingSpinner";

const HOLIDAY_LIST_SCHEMA_NAME = "PTS Holiday List";

/** Get primary key from a record (API may return id, ID, Id, _id, etc.) */
function getRecordId(record: any): string {
  if (!record || typeof record !== "object") {
    return "";
  }
  const id =
    record.id ?? record.ID ?? record.Id ?? record._id ?? record.pk ?? "";
  return id !== null ? String(id).trim() : "";
}

/** Field keys that store relation IDs; map to dropdown schema name for resolving label */
const RELATION_FIELD_SCHEMAS: Record<string, string> = {
  weekly_off: "PTS Weekly Off",
  weeklyoff: "PTS Weekly Off",
  country: "PTS Country",
};
const SYSTEM_FIELDS = [
  "id",
  "is_active",
  "created_on",
  "updated_on",
  "created_by",
  "updated_by",
  "tenant",
  "created_by_id",
  "updated_by_id",
  "tenant_id",
];

const HolidayListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [viewRecordId, setViewRecordId] = useState<string | null>(null);

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", HOLIDAY_LIST_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(HOLIDAY_LIST_SCHEMA_NAME),
  });

  const { data: viewRecord, isLoading: viewLoading } = useQuery({
    queryKey: ["holiday-view", HOLIDAY_LIST_SCHEMA_NAME, viewRecordId],
    queryFn: () =>
      DynamicModuleService.getItemById(HOLIDAY_LIST_SCHEMA_NAME, viewRecordId!),
    enabled: !!viewRecordId,
  });

  const schemaNamesToFetch = useMemo(
    () => [...new Set(Object.values(RELATION_FIELD_SCHEMAS))],
    []
  );
  const dropdownQueries = useQueries({
    queries: schemaNamesToFetch.map(schemaName => ({
      queryKey: ["dropdown", schemaName],
      queryFn: () => DropdownService.getDropdownData(schemaName),
      enabled: !!viewRecordId && !!schemaName,
    })),
  });
  const dropdownOptionsBySchema = useMemo(() => {
    const map: Record<string, { value: string; label: string }[]> = {};
    schemaNamesToFetch.forEach((name, i) => {
      const data = dropdownQueries[i]?.data;
      map[name] = Array.isArray(data) ? data : [];
    });
    return map;
  }, [schemaNamesToFetch, dropdownQueries]);

  const formatDate = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "-";
      }
    },
    []
  );

  const handleView = useCallback((id: string | undefined) => {
    if (!id) {
      notify.error("Holiday ID is missing");
      return;
    }
    setViewRecordId(String(id));
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedRowKeys?.length) {
      notify.error("No rows selected");
      return;
    }
    const ids = selectedRowKeys.map(k => String(k)).filter(Boolean);
    if (!ids.length) {
      notify.error("No valid IDs to delete");
      return;
    }
    try {
      await DynamicModuleService.bulkDeleteItems(HOLIDAY_LIST_SCHEMA_NAME, ids);
      notify.success(
        ids.length === 1
          ? "Holiday deleted successfully"
          : `${ids.length} holidays deleted successfully`
      );
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({
        queryKey: ["moduleData", HOLIDAY_LIST_SCHEMA_NAME],
      });
    } catch (error: any) {
      notify.error(error?.message ?? "Failed to delete selected holidays");
    }
  }, [selectedRowKeys, queryClient]);

  const columns: ProColumns<any>[] = useMemo(() => {
    const handleDelete = async (id: string | undefined) => {
      const idStr = id !== null && id !== "" ? String(id).trim() : "";
      if (!idStr) {
        notify.error("Holiday ID is missing");
        return;
      }
      try {
        // Delete only this one record by its id – backend must remove just this row
        await DynamicModuleService.deleteItem(HOLIDAY_LIST_SCHEMA_NAME, idStr);
        notify.success("Holiday deleted successfully");
        // Invalidate so the table refetches and shows remaining data (other rows stay)
        queryClient.invalidateQueries({
          queryKey: ["moduleData", HOLIDAY_LIST_SCHEMA_NAME],
        });
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete holiday");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Holiday ID is missing");
        return;
      }
      navigate(PTSNavigationRoutes.EDIT_HOLIDAY(String(id)));
    };

    if (!columnGridData?.grid) {
      return [];
    }

    const dataColumns: ProColumns<any>[] = columnGridData.grid
      .filter(
        (field: any) =>
          !SYSTEM_FIELDS.includes(String(field.field_name).toLowerCase())
      )
      .map((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title ?? field.field_name;
        const isDateField =
          field.field_type?.toLowerCase() === "date" ||
          field.field_type?.toLowerCase() === "datetime" ||
          fieldName.includes("date");

        const col: ProColumns<any> = {
          title: fieldTitle,
          dataIndex: fieldName,
          key: fieldName,
        };
        if (isDateField) {
          col.render = (_: any, entity: any) => {
            const value = entity?.[fieldName] ?? entity?.[field.field_name];
            return formatDate(value);
          };
        }
        return col;
      });

    return [
      ...dataColumns,
      {
        title: "Actions",
        width: 150,
        valueType: "option",
        key: "actions",
        fixed: "right" as const,
        render: (_: any, record: any) => {
          const recordIdStr = getRecordId(record);
          return [
            <CustomButton
              key="view"
              icon={<Eye size={16} />}
              type="text"
              onClick={() => handleView(recordIdStr)}
              title="View all details"
            />,
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => handleEdit(recordIdStr)}
              title="Edit Holiday"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this holiday?"
              onConfirm={() => handleDelete(recordIdStr)}
              okText="Yes"
              cancelText="No"
            >
              <span>
                <CustomButton
                  danger
                  icon={<Trash size={16} />}
                  type="text"
                  title="Delete Holiday"
                />
              </span>
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [columnGridData, queryClient, navigate, formatDate, handleView]);

  const toolBarRender = useCallback(
    () => [
      ...(selectedRowKeys?.length
        ? [
            <Popconfirm
              key="bulk-delete"
              title={`Delete ${selectedRowKeys.length} selected item(s)?`}
              onConfirm={handleBulkDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button
                key="delete-selected"
                type="button"
                variant="destructive"
                className="flex items-center gap-2"
              >
                <Trash className="h-4 w-4" />
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>,
          ]
        : []),
    ],
    [selectedRowKeys, handleBulkDelete]
  );

  // const searchFilter = useMemo(() => {
  //   const filter: Record<string, any> = {};
  //   if (searchQuery?.trim()) {
  //     filter.search = searchQuery.trim();
  //   }
  //   return filter;
  // }, [searchQuery]);

  // const toolBarRender = () => [
  //   <div key="search" className="relative">
  //     <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
  //     <Input
  //       type="text"
  //       placeholder="Search"
  //       value={searchQuery}
  //       onChange={e => setSearchQuery(e.target.value)}
  //       className="w-64 pl-10"
  //     />
  //   </div>,
  // ];

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Holiday List
          </h1>
          <p className="text-sm text-[#627384]">
            Manage holidays and non-working days
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate(PTSNavigationRoutes.CREATE_HOLIDAY)}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
          >
            <Plus className="h-4 w-4" />
            Add New Holiday
          </Button>
          <Button
            onClick={() => navigate(PTSNavigationRoutes.IMPORT_HOLIDAY)}
            className="border-primary hover:bg-primary text-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:text-white"
          >
            <Plus className="h-4 w-4" />
            Add Import
          </Button>
        </div>
      </div>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <ConfigDynamicTable
            tableName={HOLIDAY_LIST_SCHEMA_NAME}
            columns={columns}
            headerTitle=""
            rowKey={(record: any) => {
              const id = getRecordId(record);
              if (id) {
                return id;
              }
              return (
                `row-${record?.date ?? ""}-${record?.description ?? ""}`.trim() ||
                "row-fallback"
              );
            }}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            toolBarRender={toolBarRender}
          />
        </CardContent>
      </Card>

      <Modal
        title="Holiday list – all details"
        open={!!viewRecordId}
        onCancel={() => setViewRecordId(null)}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              key="close"
              variant="outline"
              onClick={() => setViewRecordId(null)}
            >
              Close
            </Button>
            <Button
              key="edit"
              className="bg-primary text-white"
              onClick={() => {
                if (viewRecordId) {
                  navigate(PTSNavigationRoutes.EDIT_HOLIDAY(viewRecordId));
                }
                setViewRecordId(null);
              }}
            >
              Edit
            </Button>
          </div>
        }
        width={640}
        destroyOnClose
      >
        {viewLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : viewRecord ? (
          <div className="space-y-6">
            {(() => {
              const gridByKey = new Map<
                string,
                { title: string; isDate: boolean }
              >();
              columnGridData?.grid?.forEach((field: any) => {
                const name = (field.field_name ?? "").toLowerCase();
                if (!name) {
                  return;
                }
                gridByKey.set(name, {
                  title: field.field_title ?? field.field_name ?? name,
                  isDate:
                    field.field_type?.toLowerCase() === "date" ||
                    field.field_type?.toLowerCase() === "datetime" ||
                    String(name).includes("date"),
                });
              });
              const childTableKeys = ["pts_holiday_child"];
              const allKeys = Object.keys(viewRecord).filter(
                k =>
                  !SYSTEM_FIELDS.includes(k.toLowerCase()) &&
                  !childTableKeys.includes(k.toLowerCase()) &&
                  !Array.isArray(viewRecord[k])
              );
              const resolveRelationValue = (
                fieldKey: string,
                idVal: unknown
              ): string => {
                if (idVal === null || idVal === "") {
                  return "–";
                }
                const schemaName =
                  RELATION_FIELD_SCHEMAS[fieldKey] ??
                  RELATION_FIELD_SCHEMAS[fieldKey.toLowerCase()];
                if (!schemaName) {
                  return String(idVal);
                }
                const options = dropdownOptionsBySchema[schemaName] ?? [];
                const option = options.find(
                  (o: { value?: string; label?: string }) =>
                    String(o?.value) === String(idVal)
                );
                return option?.label ?? String(idVal);
              };

              return allKeys.map(key => {
                const raw = viewRecord[key];
                const keyLower = key.toLowerCase();
                const meta = gridByKey.get(keyLower) ?? gridByKey.get(key);
                const title =
                  meta?.title ??
                  key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                const isDate =
                  meta?.isDate ??
                  (/date|_on$/i.test(key) ||
                    (typeof raw === "string" &&
                      /^\d{4}-\d{2}-\d{2}/.test(raw)));
                const isRelation =
                  keyLower in RELATION_FIELD_SCHEMAS ||
                  key in RELATION_FIELD_SCHEMAS;
                const value =
                  raw === null || raw === ""
                    ? "–"
                    : isRelation
                      ? resolveRelationValue(keyLower, raw)
                      : isDate
                        ? formatDate(raw)
                        : String(raw);
                return (
                  <div key={key} className="flex flex-col gap-1">
                    <span className="text-xs font-medium uppercase text-gray-500">
                      {title}
                    </span>
                    <span className="text-sm text-gray-900">{value}</span>
                  </div>
                );
              });
            })()}
            {(() => {
              const childKeys = ["pts_holiday_child", "pts_holiday_child_list"];
              let childRows: any[] = [];
              for (const ck of childKeys) {
                const val = viewRecord[ck];
                if (Array.isArray(val) && val.length > 0) {
                  childRows = val;
                  break;
                }
              }
              const isWeekendRow = (row: any) => {
                const d = (row?.description ?? row?.Description ?? "")
                  .toString()
                  .trim()
                  .toLowerCase();
                return d === "saturday" || d === "sunday";
              };
              const holidayRows = childRows.filter(
                (r: any) => !isWeekendRow(r)
              );
              const weekendRows = childRows.filter((r: any) => isWeekendRow(r));

              const renderChildTable = (
                rows: any[],
                type: "holiday" | "weekend"
              ) => (
                <div className="rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
                    <span className="text-sm font-semibold text-gray-800">
                      {type === "holiday"
                        ? "Selected Pts_holiday_child List (Holidays)"
                        : "Selected Pts_holiday_child List (Weekends)"}
                    </span>
                  </div>
                  <div className="max-h-52 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-200">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-800">
                            Date
                          </th>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-800">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={2}
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No data
                            </td>
                          </tr>
                        ) : (
                          rows.map((row: any) => (
                            <tr
                              key={
                                row?.id ??
                                row?.ID ??
                                `${String(row?.date ?? row?.Date ?? "")}-${String(row?.description ?? row?.Description ?? "")}`
                              }
                              className="border-b border-gray-100 hover:bg-gray-50"
                            >
                              <td className="px-4 py-2.5">
                                {formatDate(row?.date ?? row?.Date)}
                              </td>
                              <td className="px-4 py-2.5">
                                {row?.description ?? row?.Description ?? "–"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              if (holidayRows.length === 0 && weekendRows.length === 0) {
                return null;
              }
              return (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="mb-3 text-base font-semibold text-gray-900">
                    Add Holiday Data:
                  </h3>
                  <Tabs
                    defaultActiveKey="holiday"
                    items={[
                      {
                        key: "holiday",
                        label: "Holiday List",
                        children: (
                          <div className="pt-2">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              Holidays
                            </p>
                            {renderChildTable(holidayRows, "holiday")}
                          </div>
                        ),
                      },
                      {
                        key: "weekend",
                        label: "Weekend List",
                        children: (
                          <div className="pt-2">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              Weekends
                            </p>
                            {renderChildTable(weekendRows, "weekend")}
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              );
            })()}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default HolidayListPage;
